/**
 * Access decision — pure module.
 *
 * Given a token's claims and the app's requirements, decide whether the user
 * may use the app. No React, no globals, no side effects beyond a warn log.
 *
 * This is the seam `useAuthGuardState` (auth-core.hook.ts) delegates its
 * claim-gating to. CPD-7's "access pending" state lands here as another
 * `AccessDecision` branch — not another `useEffect`.
 */

export type AccessDecision = "granted" | "denied";

export interface AccessRequirements {
  /** A claim that must equal `value`, or (for array claims) contain it. */
  claim?: { name?: string; value?: string };
  /** A claim that must be present and non-null on the token. */
  userClaimName?: string;
}

/**
 * Decode a JWT payload without verifying the signature. Returns `{}` for any
 * malformed input (missing segment, bad base64, non-JSON) rather than throwing.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const base64 = token.split(".")[1];
    if (!base64) return {};
    const json = atob(base64.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return {};
  }
}

/** Decide access from a decoded claims object and the configured requirements. */
export function evaluateAccess(
  claims: Record<string, unknown>,
  requirements: AccessRequirements
): AccessDecision {
  const requiredName = requirements.claim?.name;
  const requiredValue = requirements.claim?.value;

  if (requiredName && requiredValue) {
    const actual = claims[requiredName];
    const matches =
      actual === requiredValue || (Array.isArray(actual) && actual.includes(requiredValue));
    if (!matches) return "denied";
  }

  if (requirements.userClaimName && claims[requirements.userClaimName] == null) {
    console.warn(`[auth] missing required user claim: ${requirements.userClaimName}`);
    return "denied";
  }

  return "granted";
}

/** Project the runtime-config shape onto the requirements this module understands. */
export function accessRequirementsFromConfig(config: {
  requireClaim?: { name?: string; value?: string };
  requireUserClaimName?: string;
}): AccessRequirements {
  return {
    claim: config.requireClaim,
    userClaimName: config.requireUserClaimName,
  };
}
